import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Download, Copy, ImageOff } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";

const ASSETS = [
  {
    name: "Instagram & Facebook Square Post",
    file: "instagram-square-1080.jpg",
    size: "1080 × 1080",
    aspect: "1:1",
    use: "Instagram feed, Facebook post, LinkedIn carousel slide. Strong 'Join the Network' CTA — best for direct sign-up campaigns.",
  },
  {
    name: "Facebook Horizontal Post",
    file: "facebook-post-1200x630.jpg",
    size: "1200 × 630",
    aspect: "1.91:1",
    use: "Facebook timeline post, link share preview, also works for LinkedIn feed posts. 'Order Local' headline.",
  },
  {
    name: "LinkedIn Professional Post",
    file: "linkedin-post-1200x627.jpg",
    size: "1200 × 627",
    aspect: "1.91:1",
    use: "LinkedIn feed posts and company updates. Wholesale buyer angle — pitch to restaurants, stores, CSA coordinators.",
  },
  {
    name: "Instagram Story / Pinterest Pin",
    file: "instagram-story-1080x1920.jpg",
    size: "1080 × 1920",
    aspect: "9:16",
    use: "Instagram Stories, Reels covers, TikTok covers, Pinterest pins. Vertical-first composition.",
  },
  {
    name: "Facebook Page Cover",
    file: "facebook-cover-1640x624.jpg",
    size: "1640 × 624",
    aspect: "~2.7:1",
    use: "Facebook page cover photo, LinkedIn company page banner (crop to 1128×191 for LinkedIn). Brand statement.",
  },
];

const OG_ASSETS = [
  { name: "Landing OG", file: "../og-default.jpg", path: "/og-default.jpg", size: "1200 × 630" },
  { name: "Drivers OG", file: "../og-drivers.jpg", path: "/og-drivers.jpg", size: "1200 × 630" },
  { name: "Wholesale OG", file: "../og-wholesale.jpg", path: "/og-wholesale.jpg", size: "1200 × 630" },
  { name: "Marketplace OG", file: "../og-marketplace.jpg", path: "/og-marketplace.jpg", size: "1200 × 630" },
];

export default function MarketingKit() {
  const { user, loading } = useAuth();
  const [available, setAvailable] = useState({});

  useEffect(() => {
    const check = async (a) => {
      try {
        const path = a.path || `/marketing/${a.file}`;
        const r = await fetch(path, { method: "HEAD" });
        return [path, r.ok];
      } catch {
        return [a.path || `/marketing/${a.file}`, false];
      }
    };
    Promise.all([...ASSETS, ...OG_ASSETS].map(check)).then((entries) => {
      const m = {};
      entries.forEach(([p, ok]) => { m[p] = ok; });
      setAvailable(m);
    });
  }, []);

  if (loading) return <div className="p-10 font-sans text-stone-500">Loading...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const download = (src, filename) => {
    const a = document.createElement("a");
    a.href = src;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const copyUrl = (path) => {
    const full = `${window.location.origin}${path}`;
    navigator.clipboard.writeText(full);
    toast.success("Image URL copied.");
  };

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-12 py-12">
      <Seo url="/marketing-kit" title="Marketing Kit" noindex />
      <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">Admin · Marketing Kit</div>
      <h1 className="mt-4 font-serif text-4xl sm:text-5xl text-stone-900">Social media assets, ready to post.</h1>
      <p className="mt-3 font-sans text-stone-600 max-w-2xl">
        Download platform-sized images for Instagram, Facebook, LinkedIn, and Pinterest. Each asset is generated with our brand palette and headline copy baked in. Re-run <code className="bg-stone-100 px-1 rounded">python /app/backend/scripts/gen_marketing_kit.py</code> on the server to regenerate.
      </p>

      <div className="mt-12">
        <h2 className="font-serif text-2xl text-stone-900">Direct-Post Assets</h2>
        <p className="mt-1 text-sm text-stone-500 font-sans">Drop these directly into the platform's post composer.</p>
        <div className="mt-6 grid sm:grid-cols-2 gap-6">
          {ASSETS.map((a) => {
            const path = `/marketing/${a.file}`;
            const ok = available[path];
            return (
              <div key={a.file} data-testid={`mk-${a.file}`} className="bg-white border border-stone-200 rounded-md overflow-hidden">
                <div className="bg-stone-100 aspect-[4/3] flex items-center justify-center overflow-hidden">
                  {ok ? (
                    <img src={path} alt={a.name} className="w-full h-full object-contain" />
                  ) : (
                    <div className="text-stone-400 font-sans text-sm flex flex-col items-center gap-2">
                      <ImageOff size={28} />
                      Generating...
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-serif text-xl text-stone-900">{a.name}</div>
                      <div className="text-xs text-stone-500 mt-1 uppercase tracking-[0.15em] font-sans">{a.size} · {a.aspect}</div>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-stone-600 font-sans leading-relaxed">{a.use}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                      data-testid={`download-${a.file}`}
                      disabled={!ok}
                      onClick={() => download(path, a.file)}
                      className="bg-emerald-800 hover:bg-emerald-700 text-stone-50 rounded-md"
                    >
                      <Download size={14} className="mr-2" /> Download
                    </Button>
                    <Button
                      data-testid={`copy-${a.file}`}
                      disabled={!ok}
                      onClick={() => copyUrl(path)}
                      variant="outline"
                      className="border-stone-300"
                    >
                      <Copy size={14} className="mr-2" /> Copy URL
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-16">
        <h2 className="font-serif text-2xl text-stone-900">Link-Preview Assets (Open Graph)</h2>
        <p className="mt-1 text-sm text-stone-500 font-sans">These auto-render when an Azure Gypsy URL is pasted on Facebook, LinkedIn, X, iMessage.</p>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {OG_ASSETS.map((a) => {
            const ok = available[a.path];
            return (
              <div key={a.file} className="bg-white border border-stone-200 rounded-md overflow-hidden">
                <div className="bg-stone-100 aspect-[1.91/1] flex items-center justify-center overflow-hidden">
                  {ok ? (
                    <img src={a.path} alt={a.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-stone-400 font-sans text-sm">Generating...</div>
                  )}
                </div>
                <div className="p-4">
                  <div className="font-serif text-base text-stone-900">{a.name}</div>
                  <div className="text-xs text-stone-500 mt-0.5 font-sans">{a.size}</div>
                  <div className="mt-3 flex gap-2">
                    <button disabled={!ok} onClick={() => download(a.path, a.file.replace("../", ""))} className="text-xs text-emerald-800 hover:underline disabled:text-stone-400">Download</button>
                    <span className="text-stone-300">·</span>
                    <button disabled={!ok} onClick={() => copyUrl(a.path)} className="text-xs text-emerald-800 hover:underline disabled:text-stone-400">Copy URL</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
